import { computed, inject, Injectable, NgZone, OnDestroy, signal } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { SessionStatus } from '@/core/model/session-status';
import { SESSION_MONITORING_TOKEN } from '@/core/util/session-monitor.token';
import { environment } from '../../../environments/environment';

const LAST_ACTION_KEY = `${environment.appKeyPrefix}:session_last_action`;

@Injectable({
    providedIn: 'root'
})
export class SessionMonitorService implements OnDestroy {
    private lastAction = signal(Date.now());
    private status = signal<SessionStatus>(SessionStatus.Active);
    readonly sessionStatus = this.status.asReadonly();
    private now = signal(Date.now());
    private lastNow = signal(Date.now());
    private timerSubscription: Subscription | null = null;
    private enabled = signal(false);
    readonly config = inject(SESSION_MONITORING_TOKEN);
    private activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    private boundActivityHandler = () => this.resetActivity();

    constructor(private zone: NgZone) {
        this.enabled.set(this.config.enabled);
        if (this.enabled()) {
            this.initTimer();
        }
    }

    initTimer() {
        const restored = this.restoreLastAction();

        if (!restored) {
            this.lastAction.set(Date.now());
        }

        this.status.set(SessionStatus.Active);
        this.stopTimer();
        this.startTimer();
        this.listenToActivity();
        this.check(); // evaluate immediately on init so status reflects restored elapsed
    }

    // ─── Persistence ────────────────────────────────────────────────────────────

    private restoreLastAction(): boolean {
        const stored = sessionStorage.getItem(LAST_ACTION_KEY);
        if (stored) {
            const parsed = parseInt(stored, 10);
            if (!isNaN(parsed)) {
                this.lastAction.set(parsed);
                return true;
            }
        }
        return false;
    }

    private persistLastAction() {
        sessionStorage.setItem(LAST_ACTION_KEY, String(this.lastAction()));
    }

    private clearPersistedLastAction() {
        sessionStorage.removeItem(LAST_ACTION_KEY);
    }

    // ─── Timer ──────────────────────────────────────────────────────────────────

    private startTimer() {
        if (this.timerSubscription) return;
        this.zone.runOutsideAngular(() => {
            this.timerSubscription = interval(1000).subscribe(() => {
                this.now.set(Date.now());
                this.check();
            });
        });
    }

    private stopTimer() {
        this.timerSubscription?.unsubscribe();
        this.timerSubscription = null;
    }

    private check() {
        const elapsed = this.now() - this.lastAction();
        if (this.status() === SessionStatus.LogoutPrompt) {
            this.stopTimer();
            this.clearPersistedLastAction();
            return;
        }

        if (elapsed >= this.config.logoutThresholdMs) {
            this.zone.run(() => this.status.set(SessionStatus.LogoutPrompt));
            this.stopTimer();
            this.clearPersistedLastAction();
        } else if (elapsed >= this.config.reloginThresholdMs && this.status() === SessionStatus.Active) {
            this.lastNow.set(elapsed);
            this.zone.run(() => this.status.set(SessionStatus.ReloginPrompt));
            this.persistLastAction(); // only persist when crossing into ReloginPrompt
        }
    }

    // ─── Activity ───────────────────────────────────────────────────────────────

    resetActivity() {
        switch (this.status()) {
            case SessionStatus.Active:
                this.lastAction.set(Date.now());
                this.clearPersistedLastAction();
                break;
            case SessionStatus.ReloginPrompt:
                // Reset elapsed back to reloginThresholdMs exactly
                // so the countdown continues from that point, not from 0
                this.lastAction.set(Date.now() - this.config.reloginThresholdMs);
                this.persistLastAction(); // keep persisted since still in ReloginPrompt
                break;
            default:
                return;
        }
    }

    private listenToActivity() {
        this.stopListeningToActivity();
        this.zone.runOutsideAngular(() => {
            this.activityEvents.forEach((event) => {
                window.addEventListener(event, this.boundActivityHandler, { passive: true });
            });
        });
    }

    private stopListeningToActivity() {
        this.activityEvents.forEach((event) => {
            window.removeEventListener(event, this.boundActivityHandler);
        });
    }

    // ─── Public API ─────────────────────────────────────────────────────────────

    triggerDuplicateSession() {
        this.status.set(SessionStatus.DuplicateSession);
        this.clearPersistedLastAction();
    }

    readonly timeUntilLogout = computed(() => {
        const elapsed = this.now() - this.lastAction();
        return Math.max(this.config.logoutThresholdMs - elapsed, 0);
    });

    setStatus(status: SessionStatus) {
        this.status.set(status);
    }

    destroy() {
        this.stopTimer();
        this.stopListeningToActivity();
    }

    ngOnDestroy(): void {
        this.destroy();
    }
}
